import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { readQueue } from "../../platform/bugReportQueue";
import { captureScreenshot } from "../../platform/screenshot";
import {
  buildBugReport,
  flushHeldBugReports,
  submitBugReport,
} from "../../platform/bugReports";
import { useT } from "../../i18n/LanguageProvider";
import { ShopFloorIcon } from "./ShopFloorIcon";

/**
 * G-72 — "Report an error", on every kiosk screen.
 *
 * WHY IT IS HERE AND NOT A SUPPORT EMAIL. Two shops are about to run a pilot on
 * this app. A florist who hits a bug has no way to tell the owner what they saw,
 * and "it broke yesterday, I think on the sale screen" is not something anyone
 * can act on. This attaches the evidence — what the console said, which panel
 * was open, which florist, which shop — to whatever they type.
 *
 * PORTALLED TO `document.body`, deliberately. G-73 paid for this lesson: a
 * `position: fixed` overlay rendered inside a floor panel is TRAPPED by the
 * panel's `backdrop-filter`, because a filtered ancestor becomes the containing
 * block for fixed descendants. The button belongs to the kiosk; the overlay
 * must not live inside it.
 *
 * NEVER BLOCKS THE FLORIST. A failed send queues and retries on the next load
 * rather than holding them at a dialog — the report is our problem, not theirs.
 */

interface Props {
  /** Which kiosk panel is open. The kiosk is one route, so the URL cannot say. */
  screen: string;
}

export function ShopFloorBugReport({ screen }: Props) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [queued, setQueued] = useState(() => readQueue().length);
  /** The screen as it was BEFORE this dialog covered it. See `openReport`. */
  const [shot, setShot] = useState<File | null>(null);

  /**
   * Capture FIRST, then open.
   *
   * The obvious order — open the dialog, then screenshot — photographs the
   * dialog. What the owner needs is the screen the florist was complaining
   * about, which stops existing the moment the overlay is on top of it.
   */
  const openReport = useCallback(async () => {
    setShot(await captureScreenshot());
    setOpen(true);
  }, []);

  /**
   * Anything held from a previous shift goes now.
   *
   * Runs once on mount rather than on a timer: the kiosk reloads often enough
   * (every arming, every app restart) that a queue never waits long, and a
   * retry loop would keep hammering a connection that is already struggling.
   */
  useEffect(() => {
    let cancelled = false;
    void flushHeldBugReports().then((kept) => {
      if (!cancelled) setQueued(kept);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = useCallback(async () => {
    const text = message.trim();
    if (text.length === 0) return;
    setBusy(true);
    const outcome = await submitBugReport(
      buildBugReport({ message: text, kind: "REPORTED", screen }),
      shot,
    );
    if (outcome === "sent") {
      setDone(t("bug.sent"));
    } else {
      setQueued(readQueue().length);
      setDone(t("bug.saved"));
    }
    setBusy(false);
    setMessage("");
  }, [message, screen, shot, t]);

  const close = useCallback(() => {
    setOpen(false);
    setDone(null);
    // Dropped on close: an image of a customer's address should not sit in
    // memory after the dialog it belonged to is gone.
    setShot(null);
  }, []);

  return (
    <>
      {/* PORTALLED, like the overlay. A `position: fixed` element inside a
          floor panel is trapped by that panel's `backdrop-filter` — the panel
          becomes the containing block and the button drifts with the content
          instead of staying put. G-73 paid for this once; rendering at <body>
          means no ancestor can do it again, whatever the layout grows into. */}
      {createPortal(
        <button
          type="button"
          className="sf-bug-button"
          onClick={() => void openReport()}
          aria-label={t("bug.buttonAria")}
        >
          <ShopFloorIcon name="flag" size={20} />
          <span>{t("bug.button")}</span>
          {queued > 0 ? (
            /* Says the tablet is holding something, so nobody reports the same
               fault three times thinking the first two vanished. */
            <span className="sf-bug-queued" aria-label={t("bug.queued", { count: queued })}>
              {queued}
            </span>
          ) : null}
        </button>,
        document.body,
      )}

      {open
        ? createPortal(
            <div
              className="sf-bouquet-modal-backdrop"
              role="presentation"
              onClick={(e) => {
                if (e.target === e.currentTarget) close();
              }}
            >
              <div
                className="sf-bouquet-modal sf-bug-modal"
                role="dialog"
                aria-modal="true"
                aria-label={t("bug.button")}
              >
                <h2 className="sf-bug-title">{t("bug.title")}</h2>
                {done === null ? (
                  <>
                    <textarea
                      className="sf-bug-input"
                      rows={4}
                      autoFocus
                      value={message}
                      maxLength={4000}
                      placeholder={t("bug.placeholder")}
                      onChange={(e) => setMessage(e.target.value)}
                      disabled={busy}
                    />
                    {/* Said plainly, because it leaves the shop — and it names
                        the picture when there IS one. A florist who later
                        discovers a screenshot went without being told would be
                        right to stop using this. */}
                    <p className="muted sf-bug-note">
                      {shot ? t("bug.sentWithShot") : t("bug.sentWithout")}{" "}
                      {t("bug.redacted")}
                    </p>
                    <div className="sf-bug-actions">
                      <button
                        type="button"
                        className="button"
                        onClick={() => void submit()}
                        disabled={busy || message.trim().length === 0}
                      >
                        {busy ? t("bug.sending") : t("bug.send")}
                      </button>
                      <button
                        type="button"
                        className="button ghost"
                        onClick={close}
                        disabled={busy}
                      >
                        {t("common.cancel")}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="sf-bug-done">{done}</p>
                    <div className="sf-bug-actions">
                      <button type="button" className="button" onClick={close}>
                        {t("common.close")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
